import React, { useState, useEffect } from 'react';
import { CheckIcon, CloseIcon } from '../Icons';
import '../filterComponents.css';

const TextInputFilterTW = ({
    type,
    label,
    filterKey,
    deletable = true,
    onFilterChange,
    currentValue,
    placeholder = 'Enter value...',
    formatValue = (value) => value,
    multiple = false,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedValues, setSelectedValues] = useState([]);

    useEffect(() => {
        if (currentValue) {
            if (multiple) {
                // For multiple mode, handle array of values
                const values = Array.isArray(currentValue) ? currentValue.map((v) => v.data) : [];
                setSelectedValues(values);
            } else {
                // For single mode, set the input value
                const value = currentValue.data;
                setInputValue(value);
                setSelectedValues([value]);
            }
        } else {
            // Reset state when currentValue is null
            setSelectedValues([]);
            if (!multiple) {
                setInputValue('');
            }
        }
    }, [currentValue, multiple]);

    const handleInputChange = (event) => {
        const value = event.target.value;
        setInputValue(value);

        if (!multiple) {
            const trimmedValue = value.trim();
            if (!trimmedValue) {
                onFilterChange(null);
                return;
            }

            const formattedValue = formatValue(trimmedValue);
            onFilterChange({
                type,
                data: formattedValue,
                chipLabel: `${label}: ${formattedValue}`,
                filterKey,
                deletable,
            });
        }
    };

    const handleSubmit = () => {
        const trimmedValue = inputValue.trim();
        if (!trimmedValue) return;

        const formattedValue = formatValue(trimmedValue);

        // Don't add duplicates
        if (!selectedValues.includes(formattedValue)) {
            const newValues = [...selectedValues, formattedValue];
            setSelectedValues(newValues);

            // Send all values to parent
            onFilterChange(
                newValues.map((value) => ({
                    type,
                    data: value,
                    chipLabel: `${label}: ${value}`,
                    filterKey,
                    deletable,
                }))
            );
        }

        setInputValue('');
    };

    const handleDeleteChip = (valueToDelete) => {
        const newValues = selectedValues.filter((value) => value !== valueToDelete);
        setSelectedValues(newValues);

        if (newValues.length === 0) {
            onFilterChange(null);
        } else {
            onFilterChange(
                newValues.map((value) => ({
                    type,
                    data: value,
                    chipLabel: `${label}: ${value}`,
                    filterKey,
                    deletable,
                }))
            );
        }
    };

    const handleClear = () => {
        setInputValue('');
        setSelectedValues([]);
        onFilterChange(null);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (multiple) {
                handleSubmit();
            } else if (inputValue.trim()) {
                // For single mode, just ensure the current value is applied
                handleInputChange(event);
            }
        }
    };

    return (
        <div className="flex flex-col gap-2 filter-components">
            <div className="flex gap-2 items-center">
                <input
                    className="text-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={placeholder}
                />
                {multiple ? (
                    <button
                        className="button-action button-submit"
                        onClick={handleSubmit}
                        disabled={!inputValue.trim()}
                        title="Add"
                        type="button"
                    >
                        <CheckIcon className="w-5 h-5" />
                    </button>
                ) : (
                    inputValue && (
                        <button
                            className="button-action button-clear"
                            onClick={handleClear}
                            title="Clear"
                            type="button"
                        >
                            <CloseIcon className="w-5 h-5" />
                        </button>
                    )
                )}
            </div>

            {multiple && selectedValues.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                    {selectedValues.map((value) => (
                        <div key={value} className="filter-chip header-match">
                            <span>{value}</span>
                            <button onClick={() => handleDeleteChip(value)} title="Remove" type="button">
                                <CloseIcon />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export { TextInputFilterTW };
